import { Check, ChevronRight } from 'lucide-react'
import { DropdownMenu as DropdownMenuPrimitive } from 'radix-ui'
import {
  forwardRef,
  useEffect,
  useRef,
  type ComponentPropsWithoutRef,
  type ComponentRef,
  type HTMLAttributes,
  type ReactNode,
  type RefObject
} from 'react'
import {
  MENU_CHOICE_CHECK_CLASS,
  MENU_CHOICE_ITEM_CLASS,
  MENU_ITEM_CLASS,
  MENU_ITEM_DESTRUCTIVE_CLASS,
  MENU_SEPARATOR_CLASS,
  MENU_SHORTCUT_CLASS,
  MENU_SURFACE_CLASS as SHARED_MENU_SURFACE_CLASS
} from '#lib/menu'
import { SURFACE_MOTION_CLASS } from '#lib/motion'
import { cn } from '#lib/utils'
import { useLayerPortalTarget } from '#components/LayerHost'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '#components/Tooltip'

const MENU_SURFACE_CLASS = cn(
  SHARED_MENU_SURFACE_CLASS,
  'pointer-events-auto min-w-[8rem] max-h-[var(--radix-dropdown-menu-content-available-height)] max-w-[var(--radix-dropdown-menu-content-available-width)] origin-[var(--radix-dropdown-menu-content-transform-origin)]'
)

export const DropdownMenu = DropdownMenuPrimitive.Root
export const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger
export const DropdownMenuGroup = DropdownMenuPrimitive.Group
export const DropdownMenuSub = DropdownMenuPrimitive.Sub
export const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup

export interface DropdownMenuViewportTriggerProps extends Omit<
  HTMLAttributes<HTMLSpanElement>,
  'children'
> {
  x: number
  y: number
}

export const DropdownMenuViewportTrigger = forwardRef<
  HTMLSpanElement,
  DropdownMenuViewportTriggerProps
>(function DropdownMenuViewportTrigger({ className, style, x, y, ...props }, ref) {
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Trigger asChild>
        <span
          ref={ref}
          aria-hidden="true"
          {...props}
          className={cn('pointer-events-none fixed size-0', className)}
          style={{ ...style, left: x, top: y }}
        />
      </DropdownMenuPrimitive.Trigger>
    </DropdownMenuPrimitive.Portal>
  )
})

export const DropdownMenuSubTrigger = forwardRef<
  ComponentRef<typeof DropdownMenuPrimitive.SubTrigger>,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger> & { inset?: boolean }
>(function DropdownMenuSubTrigger({ className, inset, children, ...props }, ref) {
  return (
    <DropdownMenuPrimitive.SubTrigger
      ref={ref}
      className={cn(MENU_ITEM_CLASS, 'data-[state=open]:bg-accent/50', inset && 'pl-8', className)}
      {...props}
    >
      <span className="flex min-w-0 flex-1 items-center gap-2">{children}</span>
      <ChevronRight className="size-3" />
    </DropdownMenuPrimitive.SubTrigger>
  )
})

export const DropdownMenuSubContent = forwardRef<
  ComponentRef<typeof DropdownMenuPrimitive.SubContent>,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent>
>(function DropdownMenuSubContent(
  { className, style, sideOffset = 4, collisionPadding = 8, collisionBoundary, ...props },
  ref
) {
  const transientTarget = useLayerPortalTarget('transient')
  return (
    <DropdownMenuPrimitive.Portal container={transientTarget.container ?? undefined}>
      <DropdownMenuPrimitive.SubContent
        ref={ref}
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        collisionBoundary={collisionBoundary ?? transientTarget.container ?? undefined}
        className={cn(MENU_SURFACE_CLASS, SURFACE_MOTION_CLASS, className)}
        style={{ ...style, zIndex: transientTarget.zIndex }}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  )
})

/** Radix 让内容与触发器通过 `aria-labelledby` 关联，菜单 portal 到弹层后只能这样回溯触发器。 */
function menuTriggerOf(content: EventTarget | null): HTMLElement | null {
  const triggerId = content instanceof HTMLElement ? content.getAttribute('aria-labelledby') : null
  return triggerId ? document.getElementById(triggerId) : null
}

/**
 * 记录菜单打开期间最后一次输入方式。监听只在菜单挂载期间存在，每次打开从指针
 * 重新开始；子菜单和菜单外点击都经 document 捕获，不依赖事件冒泡到某个容器。
 */
function useMenuKeyboardModality(): RefObject<boolean> {
  const keyboardDriven = useRef(false)
  useEffect(() => {
    const markPointer = (): void => {
      keyboardDriven.current = false
    }
    const markKeyboard = (): void => {
      keyboardDriven.current = true
    }
    document.addEventListener('pointerdown', markPointer, true)
    document.addEventListener('keydown', markKeyboard, true)
    return () => {
      document.removeEventListener('pointerdown', markPointer, true)
      document.removeEventListener('keydown', markKeyboard, true)
    }
  }, [])
  return keyboardDriven
}

export const DropdownMenuContent = forwardRef<
  ComponentRef<typeof DropdownMenuPrimitive.Content>,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>
>(function DropdownMenuContent(
  {
    className,
    sideOffset = 4,
    collisionPadding = 8,
    collisionBoundary,
    style,
    onCloseAutoFocus,
    ...props
  },
  ref
) {
  const transientTarget = useLayerPortalTarget('transient')
  const keyboardDriven = useMenuKeyboardModality()
  return (
    <DropdownMenuPrimitive.Portal container={transientTarget.container ?? undefined}>
      <DropdownMenuPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        collisionBoundary={collisionBoundary ?? transientTarget.container ?? undefined}
        className={cn(MENU_SURFACE_CLASS, SURFACE_MOTION_CLASS, className)}
        style={{ ...style, zIndex: transientTarget.zIndex }}
        {...props}
        onCloseAutoFocus={(event) => {
          onCloseAutoFocus?.(event)
          if (event.defaultPrevented) return
          // 指针操作关闭后 Radix 仍把焦点还给触发器。归还的瞬间没有描边，但触发器
          // 会静默持有焦点，作者之后随便按一个键，Chromium 就给当前焦点元素匹配
          // :focus-visible，触发器凭空多出一圈描边。键盘操作关闭时焦点必须回到
          // 触发器，那圈描边正是键盘用户需要的。
          if (keyboardDriven.current) return
          // 触发器位于会话框等焦点陷阱内时仍然归还：菜单 portal 在弹窗之外，
          // Radix FocusScope 的 focusout 只在 relatedTarget 非空时把焦点拉回来，
          // 跳过归还会让焦点停在 body，直接掉出打开着的弹窗。回溯不到触发器时
          // 同样保持 Radix 默认。
          const trigger = menuTriggerOf(event.target)
          if (!trigger || trigger.closest('[role="dialog"], [role="alertdialog"]')) return
          event.preventDefault()
        }}
      />
    </DropdownMenuPrimitive.Portal>
  )
})

export const DropdownMenuItem = forwardRef<
  ComponentRef<typeof DropdownMenuPrimitive.Item>,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & {
    inset?: boolean
    destructive?: boolean
  }
>(function DropdownMenuItem({ className, inset, destructive, ...props }, ref) {
  return (
    <DropdownMenuPrimitive.Item
      ref={ref}
      className={cn(
        MENU_ITEM_CLASS,
        "aria-[current=page]:bg-accent aria-[current=page]:font-medium aria-[current=page]:[&_svg:not([class*='text-'])]:text-foreground",
        inset && 'pl-8',
        destructive && MENU_ITEM_DESTRUCTIVE_CLASS,
        className
      )}
      {...props}
    />
  )
})

export const DropdownMenuCheckboxItem = forwardRef<
  ComponentRef<typeof DropdownMenuPrimitive.CheckboxItem>,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem>
>(function DropdownMenuCheckboxItem({ className, children, checked, ...props }, ref) {
  return (
    <DropdownMenuPrimitive.CheckboxItem
      ref={ref}
      className={cn(MENU_ITEM_CLASS, 'pl-8', className)}
      checked={checked}
      {...props}
    >
      <span className="absolute left-2.5 flex size-3.5 items-center justify-center">
        <DropdownMenuPrimitive.ItemIndicator>
          <Check aria-hidden="true" className="text-foreground" />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.CheckboxItem>
  )
})

/** 单选项：选中项右侧对勾加浅底，与共享 Select 的弹层一致。 */
export const DropdownMenuRadioItem = forwardRef<
  ComponentRef<typeof DropdownMenuPrimitive.RadioItem>,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem>
>(function DropdownMenuRadioItem({ className, children, ...props }, ref) {
  return (
    <DropdownMenuPrimitive.RadioItem
      ref={ref}
      className={cn(MENU_ITEM_CLASS, MENU_CHOICE_ITEM_CLASS, className)}
      {...props}
    >
      {children}
      <span className={MENU_CHOICE_CHECK_CLASS}>
        <DropdownMenuPrimitive.ItemIndicator>
          <Check aria-hidden="true" className="text-foreground" />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
    </DropdownMenuPrimitive.RadioItem>
  )
})

export interface DropdownMenuSegmentedRadioOption<T extends string> {
  value: T
  label: string
  content: ReactNode
}

export interface DropdownMenuSegmentedRadioProps<T extends string> {
  value: T
  options: ReadonlyArray<DropdownMenuSegmentedRadioOption<T>>
  onValueChange: (value: T) => void
  ariaLabel: string
  className?: string
}

/**
 * 菜单行内的分段单选。选项仍是 menuitemradio：Radix 菜单拦截 Tab，普通按钮放进
 * 菜单会失去键盘入口，而菜单项沿方向键焦点顺序可达。选择立即生效并保持菜单打开，
 * 作者可以连续比较各个选项。
 */
export function DropdownMenuSegmentedRadio<T extends string>({
  value,
  options,
  onValueChange,
  ariaLabel,
  className
}: DropdownMenuSegmentedRadioProps<T>): React.JSX.Element {
  return (
    <DropdownMenuPrimitive.RadioGroup
      value={value}
      aria-label={ariaLabel}
      className={cn(
        'inline-flex shrink-0 overflow-hidden rounded-md border border-border bg-card',
        className
      )}
    >
      <TooltipProvider delayDuration={0}>
        {options.map((option) => (
          <Tooltip key={option.value}>
            <TooltipTrigger asChild>
              <DropdownMenuPrimitive.RadioItem
                value={option.value}
                aria-label={option.label}
                textValue={option.label}
                onSelect={(event) => {
                  event.preventDefault()
                  if (option.value !== value) onValueChange(option.value)
                }}
                className="inline-flex h-6 min-w-7 cursor-default select-none items-center justify-center px-1.5 text-muted-foreground outline-none transition-colors focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring data-[state=unchecked]:focus:bg-accent data-[state=unchecked]:focus:text-accent-foreground data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
              >
                {option.content}
              </DropdownMenuPrimitive.RadioItem>
            </TooltipTrigger>
            <TooltipContent>{option.label}</TooltipContent>
          </Tooltip>
        ))}
      </TooltipProvider>
    </DropdownMenuPrimitive.RadioGroup>
  )
}

export const DropdownMenuLabel = forwardRef<
  ComponentRef<typeof DropdownMenuPrimitive.Label>,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> & {
    inset?: boolean
    variant?: 'default' | 'muted'
  }
>(function DropdownMenuLabel({ className, inset, variant = 'default', ...props }, ref) {
  return (
    <DropdownMenuPrimitive.Label
      ref={ref}
      className={cn(
        'px-2.5 py-1.5 text-xs font-medium',
        variant === 'muted' && 'font-normal text-muted-foreground',
        inset && 'pl-8',
        className
      )}
      {...props}
    />
  )
})

export const DropdownMenuSeparator = forwardRef<
  ComponentRef<typeof DropdownMenuPrimitive.Separator>,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(function DropdownMenuSeparator({ className, ...props }, ref) {
  return (
    <DropdownMenuPrimitive.Separator
      ref={ref}
      className={cn(MENU_SEPARATOR_CLASS, className)}
      {...props}
    />
  )
})

export function DropdownMenuShortcut({
  className,
  ...props
}: HTMLAttributes<HTMLSpanElement>): React.JSX.Element {
  return <span data-menu-shortcut="" className={cn(MENU_SHORTCUT_CLASS, className)} {...props} />
}
